import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CopyButtonProps {
    value: string
    label: string
    className?: string
}

const CopyButton: FC<CopyButtonProps> = ({ value, label, className }): ReactNode => {
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 1800)

        return () => window.clearTimeout(timer)
    }, [copied])

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value)
            setCopied(true)
        } catch {
            setCopied(false)
        }
    }

    return (
        <button
            type='button'
            onClick={handleCopy}
            aria-label={`Copy ${label} colour code ${value}`}
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
                className
            )}
        >
            {copied ? <Check className='size-3.5' aria-hidden='true' /> : <Copy className='size-3.5' aria-hidden='true' />}
            {copied ? 'Copied' : value}
            <span className='sr-only' aria-live='polite'>
                {copied ? `${label} colour code copied` : ''}
            </span>
        </button>
    )
}

export default CopyButton
